<template id="ProjectTaggingMainContainer">
  <header>
    <div class="page-title">
      <h3>Project Tagging</h3>
      <h6>View or create projects, and assign members in various projects for employees to log their progress.</h6>
    </div>
    <MDBBtn :class="buttonClass" id="CreateProject" :ripple="false" @click="handleClick"> <span class="SproutIcon SproutIcon-plus-regular"></span> Create a Project </MDBBtn>
  </header>
  <main id="ProjectTaggingMain">
    <div class="filter-table-container">
      <div class="search-row-container">
        <ProjectTaggingMainSearchBar />
        <ProjectTaggingMainFilter />
      </div>
      <ProjectTaggingMainTable />
      <ProjectTaggingModal />
      <ProjectTaggingMainDrawer />
      <ProjectTaggingViewProject />
      <SPAToast variant="success" v-model="toastState" :animation="false">
        <template #title> Project Created! </template>
        <template #description> You have successfully created the {{ toastProjectName }} project. </template>
      </SPAToast>
      <SPAToast variant="success" v-model="toastEditState" :animation="false">
        <template #title> Changes Saved! </template>
        <template #description> You have successfully edited the details for {{ toastProjectName }}. </template>
      </SPAToast>
      <SPAToast variant="success" v-model="toastAssignAllState" :animation="false">
        <template #title> Employees Assigned! </template>
        <template #description> You've successfully designated all of the employees on this page. </template>
      </SPAToast>
      <SPAToast variant="success" v-model="toastRemoveAllState" :animation="false">
        <template #title> Employees Removed! </template>
        <template #description> You've successfully removed all of the employees on this page. </template>
      </SPAToast>
    </div>
  </main>
</template>

<script>
import SPAToast from '../../../components/SPAToast.vue';
import ProjectTaggingModal from './ProjectTaggingMainModal.vue';
import { useProjectTaggingStore } from '../../../stores/projectTagging';
import { storeToRefs } from 'pinia';
import { computed, onMounted } from 'vue';
import ProjectTaggingMainDrawer from './ProjectTaggingDrawer/ProjectTaggingDrawer.vue';
import ProjectTaggingMainSearchBar from './ProjectTaggingMainSearchBar.vue';
import ProjectTaggingMainFilter from './ProjectTaggingMainFilter.vue';
import ProjectTaggingViewProject from './ProjectTaggingViewProject/ProjectTaggingViewProject.vue';
import { useRouteAccessStore } from '../../../stores/routeAccess';
import { AccessLevelEnum } from '../../../enums/accessLevel';
import { MDBBtn } from 'mdb-vue-ui-kit';
import ProjectTaggingMainTable from './ProjectTaggingMainTable/ProjectTaggingMainTable.vue';
export default {
  components: {
    SPAToast,
    ProjectTaggingModal,
    ProjectTaggingMainDrawer,
    ProjectTaggingMainSearchBar,
    ProjectTaggingMainFilter,
    ProjectTaggingViewProject,
    MDBBtn,
    ProjectTaggingMainTable,
  },
  setup() {
    const routeAccessStore = useRouteAccessStore();
    const projectTagging = useProjectTaggingStore();
    const { toastState, toastProjectName, toastEditState, toastAssignAllState, toastRemoveAllState, projectCode, projectName, drawerState, createModalState } = storeToRefs(projectTagging);
    onMounted(() => {
      projectTagging.getProjects();
      projectTagging.getProjectStatuses();
    });
    const isViewOnly = computed(() => routeAccessStore.routeAccess.accessLevelStatus === AccessLevelEnum.viewonly);
    const isDisabled = computed(() => routeAccessStore.routeAccess.accessLevelStatus === AccessLevelEnum.disable);
    const buttonClass = computed(() => [{ disabled: isDisabled.value || isViewOnly.value }]);
    const handleClick = () => {
      projectCode.value = '';
      projectName.value = '';
      drawerState.value = false;
      createModalState.value = true;
    };
    return {
      toastState,
      toastProjectName,
      toastEditState,
      toastAssignAllState,
      toastRemoveAllState,
      buttonClass,
      handleClick,
    };
  },
};
</script>

<style lang="scss" scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  .page-title {
    color: $neutral-800;
    h3 {
      font-weight: 500;
      font-size: 24px;
      line-height: 36px;
      margin-bottom: 0;
    }
  }
}
#CreateProject {
  height: min-content;
  padding: 4px 8px;
  background-color: $green-300;
  color: $neutral-0;
  font-size: 1rem;
  text-transform: unset;
  box-shadow: none;
  &:hover {
    background-color: $green-200;
  }
  &:active {
    background-color: $green-400;
  }
  &.disabled {
    background-color: $neutral-40;
  }
}
.SproutIcon.SproutIcon-plus-regular {
  margin-right: 8px;
}
#ProjectTaggingMain {
  /* Tab Filter */
  .filter-table-container {
    display: grid;
    row-gap: 16px;
    .search-row-container {
      display: flex;
      margin-right: auto;
      column-gap: 8px;
    }
  }
  :deep(.nav-tabs .nav-link) {
    font-weight: 400;
    font-size: 16px;
    padding: 7px;
    border-radius: 4px;
    text-transform: capitalize;
    letter-spacing: 0.5px;
    display: flex;
    &#tab-Inactive.nav-link,
    &#tab-Active.nav-link {
      color: $neutral-800;
      background-color: $neutral-0;
    }
    &#tab-Active.active,
    &#tab-Inactive.active {
      background-color: $green-50;
      border-color: transparent;
      font-weight: 500;
    }
    &#tab-Active.active {
      color: $blue-300;
    }
    &#tab-Inactive.active {
      color: $orange-300;
    }
    &#tab-Active.active:focus,
    &#tab-Inactive.active:focus {
      background-color: $green-75;
      border-color: transparent;
    }
  }
  /* Tab Badge*/
  :deep(.badge) {
    margin-right: 8px;
    max-height: 16px;
    font-size: 14px;
    padding: 1.5px 5px;
    font-weight: 400;
    min-width: 32px;
    display: flex;
    place-content: center;
    line-height: 14px;
    &#ActiveCounter.bg-light {
      background-color: $blue-300 !important;
      color: $neutral-0 !important;
    }
    &#InactiveCounter.bg-warning {
      background-color: $orange-300 !important;
      color: $neutral-0 !important;
    }
  }
  /* Drawer */
  :deep(.sidenav) {
    padding-top: 78px;
    z-index: auto;
    width: 354px;
    box-shadow: 0px -4px 8px rgba(60, 91, 81, 0.16);
    .ps {
      height: 100% !important;
    }
  }
}
</style>
